import {array, mixed, object, string, ObjectSchema, ValidationError} from 'yup';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {assert} from 'Asserts';
import {DatasetType} from 'procedures/design/AssayProcedureMethodAnalysisTypes';

export interface HydratedTemplateConfigData {
  template: AnalysisTemplateGraphQL;
  config: AssayAnalysisTemplateMappings;
}

/**
 * This value is stored directly in the DB as JSON.
 *
 * Do not store fully hydrated types here (e.g., AnalysisTemplateGraphQL)
 */
export interface AssayAnalysisTemplateMappings {
  /**
   * Future versions may override this type (and provide the appropriate version number here).
   * For now, version is omitted.
   */
  // version?: never;

  tableMappings: Array<SingleAnalysisTemplateTableMapping>;
}

export function makeEmptyAnalysisTemplateMappings(): AssayAnalysisTemplateMappings {
  return {tableMappings: []};
}

/**
 * Future versions may override this type (and provide the appropriate version number here)
 */
export interface SingleAnalysisTemplateTableMapping {
  version?: never;
  datasetId: string;
  analysisTemplateInputTableId: string;
  datasetType: DatasetType;

  // A mapping of {[sourceColumnSystemName]: destColumnSystemName}
  columnMappings: Record<string, string>;
}

const isObject = (value: JSONScalar): value is Object =>
  typeof value === 'object' && value != null && !Array.isArray(value);

/**
 * Parses a JSON value from the server. If anything at all doesn't match our expected/required data
 * type, we clobber the whole thing. No self-healing is attempted.
 */

export function parseAndValidateAnalysisTemplateConfigData(
  config: JSONScalar
): AssayAnalysisTemplateMappings {
  if (!isObject(config)) {
    assert(false, 'Invalid template configuration', {config});
  }

  const mappingsSchema: ObjectSchema<AssayAnalysisTemplateMappings> = object()
    .shape({
      tableMappings: array(
        object()
          .shape({
            datasetId: string().required(),
            analysisTemplateInputTableId: string().required(),
            datasetType: mixed<DatasetType>().oneOf(Object.values(DatasetType)).required(),
            columnMappings: object()
              .test((obj) => Object.values(obj).every((val) => typeof val === 'string'))
              .required(),
          })
          .noUnknown()
          .required()
      ).required(),
    })
    .noUnknown()
    .required();

  try {
    return mappingsSchema.validateSync(config, {strict: true});
  } catch (e) {
    if (e instanceof ValidationError) {
      assert(false, 'Invalid template configuration', {config});
    }
    throw e;
  }
}
